import { useRef, useEffect } from 'react'

/**
 * Custom hook that throttles the execution of a callback function.
 *
 * @param {Function} callback - The function to be throttled.
 * @param {number} delay - The delay in milliseconds for throttling.
 *
 * @returns {Function} - A throttled version of the callback function.
 *
 * The throttled function will only execute after the specified delay has passed
 * since the last call, effectively limiting the rate at which the callback is invoked.
 * This can be useful for optimizing performance in cases like handling window resize
 * or scroll events.
 */

function useThrottle(callback, delay) {
  const timer = useRef()
  useEffect(() => (
    () => {
      clearTimeout(timer.current)
    }
  ), [])
  return (...args) => {
    if (timer.current) {
      clearTimeout(timer.current)
    }
    timer.current = setTimeout(() => {
      callback(...args)
    }, delay)
  }
}

export default useThrottle
